/**
 * Payment status helpers (API + panel).
 *
 * reverseMethod:
 * - void / local-cancel → banka/lokal iptal
 * - refund → iade
 */

export const PAYMENT_STATUS = {
  0: { label: 'Awaiting', color: '#ed6c02' },
  1: { label: 'Confirmed', color: '#2e7d32' },
  2: { label: 'Failed', color: '#d32f2f' },
  3: { label: 'Refunded', color: '#6a1b9a' },
};

/** Aynı gün void veya kart dışı lokal iptal. */
export const isCancelReverseMethod = (method) => {
  const m = String(method || '').toLowerCase();
  return m === 'void' || m === 'local-cancel';
};

export const getPaymentReverseMethod = (paymentOrTxn) => {
  if (!paymentOrTxn) return null;
  const txn =
    paymentOrTxn.transaction && typeof paymentOrTxn.transaction === 'object'
      ? paymentOrTxn.transaction
      : paymentOrTxn;
  return txn?.reverseMethod ?? null;
};

/** Status 3 ve reverseMethod iptal ise true. */
export const isPaymentCancelled = (payment) => {
  if (!payment || Number(payment.status) !== 3) return false;
  return isCancelReverseMethod(getPaymentReverseMethod(payment));
};

/** Status 3 ve reverseMethod iade (veya bilinmiyor) ise true. */
export const isPaymentRefunded = (payment) => {
  if (!payment || Number(payment.status) !== 3) return false;
  return !isCancelReverseMethod(getPaymentReverseMethod(payment));
};

export const formatReverseMethodLabel = (method, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  const m = String(method || '').toLowerCase();
  if (m === 'void') return t('payments.method_void', { _: 'İptal (void)' });
  if (m === 'local-cancel') {
    return t('payments.method_local_cancel', { _: 'İptal (lokal)' });
  }
  if (m === 'refund') return t('payments.method_refund', { _: 'İade (refund)' });
  return method || '—';
};

/**
 * @param {number|string} status
 * @param {Function} [translate]
 * @param {{ reverseMethod?: string } | object} [optsOrPayment] reverseMethod veya payment kaydı
 */
export const formatPaymentStatus = (status, translate, optsOrPayment) => {
  const key = Number(status);
  const t = translate || ((k, opts) => opts?._ || k);
  switch (key) {
    case 0:
      return t('payments.status_awaiting', { _: 'Bekliyor' });
    case 1:
      return t('payments.status_confirmed', { _: 'Onaylandı' });
    case 2:
      return t('payments.status_failed', { _: 'Başarısız' });
    case 3: {
      const reverseMethod =
        optsOrPayment?.reverseMethod ?? getPaymentReverseMethod(optsOrPayment) ?? null;
      if (isCancelReverseMethod(reverseMethod)) {
        return t('payments.status_cancelled', { _: 'İptal Edildi' });
      }
      return t('payments.status_refunded', { _: 'İade Edildi' });
    }
    default:
      return t('payments.status_unknown', { _: 'Bilinmiyor' });
  }
};

export const getPaymentStatusColor = (status, optsOrPayment) => {
  const key = Number(status);
  if (key === 3) {
    const reverseMethod =
      optsOrPayment?.reverseMethod ?? getPaymentReverseMethod(optsOrPayment) ?? null;
    if (isCancelReverseMethod(reverseMethod)) return '#455a64'; // iptal — gri-mavi
    return '#6a1b9a'; // iade — mor
  }
  return PAYMENT_STATUS[key]?.color ?? '#757575';
};

/** Panel iadesi yalnızca admin. Org/venue manager iade butonunu görmez. */
export const canRefundPayments = (permissions) => {
  return permissions?.crole === 'admin';
};

export const canRefundPaymentRecord = (payment) => {
  if (!payment) return false;
  if (Number(payment.status) !== 1) return false;
  const txn = payment.transaction ?? {};
  if (txn.refundAmount != null && Number(txn.refundAmount) >= Number(payment.total)) {
    return false;
  }
  return true;
};

/** A scheduled retry must not hide an uncertain bank result. */
export const getPaymentRefundFollowupKey = (payment) => {
  const txn = payment?.transaction || {};
  const attempts = Array.isArray(payment?.bankRefundAttempts) ? payment.bankRefundAttempts : [];
  const latest = attempts[attempts.length - 1];
  if (txn.refundDbPending) return 'payments.bank_done_record_pending';
  if (
    latest?.outcome === 'uncertain' ||
    (txn.needsManualReview && latest?.outcome !== 'confirmed')
  ) {
    return 'payments.bank_review_required';
  }
  if (txn.refundFinalFailed && latest?.outcome !== 'confirmed') {
    return 'payments.refund_attempts_stopped';
  }
  if (txn.pendingRefund) return 'payments.refund_retry_scheduled';
  if (latest?.outcome === 'declined' || (txn.refundFailed && latest?.outcome !== 'confirmed')) {
    return 'payments.no_auto_retry';
  }
  return null;
};
